import {
  DescriptionItem,
  ReportUrl,
  VehicleDetailsDTO,
} from "./vehicle-details-dto.type";
import { Report, VehicleDetailsProps } from "./vehicle-details-props.type";

const getByInvariantKey = (
  items: DescriptionItem[],
  key: string
): { label: string; value: string } => {
  const findByInvariantKey = items.filter(
    ({ invariantKey }) => invariantKey === key
  )[0];

  return {
    label: (findByInvariantKey && findByInvariantKey.label) || "",
    value: (findByInvariantKey && findByInvariantKey.information) || "",
  };
};

const getLocationUrl = (
  links: {
    rel: string;
    href: string;
  }[]
) => {
  const locationUrl = links.find((current) => {
    return current.rel === "vehiclelocation";
  });
  return Boolean(locationUrl) ? locationUrl.href : "";
};

const getBiddingType = (
  links: {
    rel: string;
    href: string;
  }[]
) => {
  const biddingType = links.filter((current) => {
    return current.rel === "proxybid";
  });
  return biddingType.length === 0 ? "BidNowBuyNow" : "ProxyBid";
};

const getBiddingUrl = (
  proxyBidRel: string,
  bidRel: string,
  links: {
    rel: string;
    href: string;
  }[]
) => {
  const biddingType = getBiddingType(links);
  return biddingType === "ProxyBid"
    ? links.filter((current) => current.rel === proxyBidRel)[0].href
    : links.filter((current) => current.rel === bidRel)[0].href;
};

const getTermsAndConditions = (
  links: {
    rel: string;
    href: string;
  }[]
) =>
  links.filter((current) => current.rel === "termsandconditionsacceptance")[0]
    .href;

const gradeMapper = {
  Unknown: 0,
  "1": 1,
  "2": 2,
  "3": 3,
  "4": 4,
  "5": 5,
  "Retail ready": 6,
  Unclassified: 7,
};

const getReport = (
  reportType:
    | "mechanicalreporturl"
    | "conditionreporturl"
    | "evbatteryreporturl",
  reports: ReportUrl[]
): Report => {
  const findByRel =
    reports.length && reports.filter(({ rel }) => rel === reportType)[0];

  return {
    type: (findByRel && findByRel.type) || "NoReport",
    href: (findByRel && findByRel.href) || "",
  };
};

const getColor = (vehicleColor: string) => {
  return vehicleColor ? vehicleColor.toLowerCase() : "-";
};

const getTransmission = (vehicleDescriptionItems: DescriptionItem[]) => {
  const input = getByInvariantKey(vehicleDescriptionItems, "Gearbox").value;
  const transmissionMapper = {
    "Manual Transmission": "Manual",
  };
  return transmissionMapper[input] || input || "-";
};

const getNumberOfOwners = (vehicleDescriptionItems: DescriptionItem[]) => {
  const owners = getByInvariantKey(
    vehicleDescriptionItems,
    "NumberOfOwners"
  ).value;

  return !owners || owners === "0" ? "-" : owners;
};

export const getVehicleDetails = ({
  vehicleDetails: {
    vrm,
    vin,
    vehicleType,
    mileageWarranted,
    equipment,
    colourGeneric: vehicleColor,
    manufacturerColour,
    vehicleDescriptionItems,
    reportUrls: reports,
    priceItems: vehiclePrices,
    isTracked,
    isFeatured,
    significantFields,
    assessment,
    declaration,
    insuranceTotalLoss,
    additionalInformation,
    equipmentItems,
    serviceHistory,
    isLatestVersion,
    hasPreviousVersion,
    soldDate,
    inventoryData: { inventoryID, dateOnSite },
  },
  saleDetails: {
    saleTypeName: name,
    publishedSaleTypeName: publishedName,
    saleChannel: type,
    saleDate: startDate,
    saleEndDate: endDate,
    localSaleName: sectionName,
    localSaleLocation: location,
    saleLocation,
    saleCode,
    saleTypeCode,
    lolSaleCode: lolCode,
    saleStatus: status,
    numberOfLotsInSection: numberOfLots,
  },
  lotDetails: { images, lotNumber, lotNote, isProvisional },
  hpi,
  links,
  printSections,
  isAllocatedToSale,
  isVendorOfVehicle,
  degradedServices,
  navigation,
  displayFlags,
}: VehicleDetailsDTO): VehicleDetailsProps => {
  return {
    navigation,
    assessment,
    analyticsProps: null,
    username: null,
    fetchError: false,
    description: getByInvariantKey(
      vehicleDescriptionItems,
      "VehicleDescription"
    ).value,
    mileage:
      parseInt(getByInvariantKey(vehicleDescriptionItems, "Mileage").value) ||
      null,
    grade:
      gradeMapper[
        getByInvariantKey(vehicleDescriptionItems, "VehicleGrade").value
      ] || gradeMapper["Unknown"],
    mechanicalReport: getReport("mechanicalreporturl", reports),
    conditionReport: getReport("conditionreporturl", reports),
    batteryReport: getReport("evbatteryreporturl", reports),
    prices: {
      CAPClean: getByInvariantKey(vehiclePrices, "CAPClean"),
      CAPAverage: getByInvariantKey(vehiclePrices, "CAPAverage"),
      CAPNew: getByInvariantKey(vehiclePrices, "CAPNew"),
      CAPRetail: getByInvariantKey(vehiclePrices, "CAPRetail"),
      CAPBelowAverage: getByInvariantKey(vehiclePrices, "CAPBelowAverage"),
      GlassRetail: getByInvariantKey(vehiclePrices, "GlassRetail"),
      GlassNew: getByInvariantKey(vehiclePrices, "GlassNew"),
      GlassTrade: getByInvariantKey(vehiclePrices, "GlassTrade"),
    },
    vehicleType: vehicleType || "-",
    vrm: vrm,
    vin: vin,
    mileageWarranted: mileageWarranted,
    color: getColor(vehicleColor),
    manufacturerColour: manufacturerColour || getColor(vehicleColor),
    fuel: getByInvariantKey(vehicleDescriptionItems, "FuelType").value || "-",
    transmission: getTransmission(vehicleDescriptionItems),
    doors:
      getByInvariantKey(vehicleDescriptionItems, "NumberOfDoors").value || "-",
    logBook:
      getByInvariantKey(vehicleDescriptionItems, "LogBookStatusIndicator")
        .value || "-",
    registrationDate:
      getByInvariantKey(vehicleDescriptionItems, "DateRegistered").value || "-",
    vatType: getByInvariantKey(vehicleDescriptionItems, "VATType").value || "-",
    equipment: equipment || "-",
    serviceHistory:
      getByInvariantKey(vehicleDescriptionItems, "ServiceHistoryIndicator")
        .value || "-",
    equipmentItems: equipmentItems || null,
    serviceHistoryData: serviceHistory || null,
    lastServiceDate:
      getByInvariantKey(vehicleDescriptionItems, "LastHistoryServiceDate")
        .value || "-",
    MOTExpiryDate:
      getByInvariantKey(vehicleDescriptionItems, "MOTDate").value || "-",
    owners: getNumberOfOwners(vehicleDescriptionItems),
    saleDetails: {
      name: name || "",
      publishedName,
      type: type || "",
      saleCode: saleCode || "",
      startDate: startDate || "",
      endDate: endDate || "",
      sectionName: sectionName || "",
      location: location || "",
      saleLocation: saleLocation || "",
      saleTypeCode,
      saleTypeName: name || "",
      lolCode,
      status,
      numberOfLots,
    },
    insuranceTotalLoss,
    images: images,
    lotNumber: lotNumber || "",
    isTracked: isTracked || false,
    isFeatured: isFeatured || false,
    lotNote: lotNote || null,
    biddingType: getBiddingType(links),
    biddingInfoUrl: getBiddingUrl("proxybiddingInfo", "biddingInfo", links),
    placeBidUrl: getBiddingUrl("proxybid", "bid", links),
    acceptTermsAndConditionsUrl: getTermsAndConditions(links),
    printSections: printSections ? printSections : [],
    userType: null,
    degradedServices,
    hpi: hpi,
    additionalInformation: additionalInformation || null,
    declaration: declaration || null,
    significantFields,
    locationUrl: getLocationUrl(links),
    isAllocatedToSale,
    isProvisional,
    isVendorOfVehicle,
    isLatestVersion: isLatestVersion || null,
    hasPreviousVersion: hasPreviousVersion || null,
    soldDate,
    displayFlags,
    inventoryData: {
      inventoryID,
      dateOnSite,
    },
  };
};
